'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ReactNode, useState } from 'react';

interface ReactQueryProviderProps {
  children: ReactNode;
}

/**
 * Extrae el status HTTP de un error de forma segura
 * Soporta tanto AxiosError como errores custom del httpClient
 */
function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;

  // AxiosError: el status está en error.response.status
  if ('response' in error && error.response && typeof error.response === 'object' && 'status' in error.response) {
    return (error as AxiosError).response?.status;
  }

  // Error custom del httpClient: el status está directamente en error.status
  if ('status' in error && typeof (error as { status: unknown }).status === 'number') {
    return (error as { status: number }).status;
  }

  return undefined;
}

/**
 * Reintenta solo errores transitorios; repetir un error HTTP 4xx no cambia el
 * resultado y genera ruido y peticiones innecesarias.
 */
export function shouldRetryRequest(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false;

  const status = getErrorStatus(error);
  if (status !== undefined && status >= 400 && status < 500) return false;

  return true;
}

/**
 * Provider for React Query
 * Configures and provides the QueryClient for the application
 */
export function ReactQueryProvider({ children }: ReactQueryProviderProps) {
  // Create a new QueryClient instance for each session to avoid shared state between users
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 15 * 60 * 1000, // 15 minutes
            retry: shouldRetryRequest,
            retryDelay: () => 20000, // Esperar 20 segundos fijos
            refetchOnWindowFocus: false,
          },
          mutations: {
            retry: shouldRetryRequest,
            retryDelay: () => 20000, // Esperar 20 segundos fijos
          },
        },
      })
  );

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
